import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, RefreshCw, Copy, Check, Calculator, ClipboardList, Trash2, X, AlertCircle, Cog, Target, Palette } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../i18n';
import { formatAdmin } from '../locales/admin';

// Role color definitions for dropdown styling
const ROLE_COLORS = {
  admin: '#d35400',
  board: '#2980b9',
  member: '#27ae60'
};

const AdminPanel = ({ user }) => {
  const { t } = useI18n(user?.club?.language);
  const a = t.admin;
  const isBoardOnly = user?.role === 'board';
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: '', role: 'member' });
  const [copied, setCopied] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [roleSaved, setRoleSaved] = useState(null);
  const [showOnlyActive, setShowOnlyActive] = useState(true); // Filter checkbox
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Delete confirmation
  const [deleteError, setDeleteError] = useState(null); // Delete error message
  const [adsAdminEnabled, setAdsAdminEnabled] = useState(false);

  useEffect(() => {
    if (isBoardOnly) return;
    fetchUsers();
    apiFetch('/api/club/beta-features')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.enabled) setAdsAdminEnabled(!!data.enabled.ads_admin);
      })
      .catch(() => {});
  }, [isBoardOnly]);

  const fetchUsers = () => {
    fetch('/api/users')
      .then(res => res.json())
      .then(setUsers)
      .catch(console.error);
  };
  
  // Filter users based on active status
  const filteredUsers = showOnlyActive ? users.filter(u => u.isActive) : users;

  const addUser = () => {
    fetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newUser })
    })
    .then(res => {
      if(res.ok) {
        alert(a.panel.userAdded);
        setNewUser({ name: '', role: 'member' });
        fetchUsers();
      } else {
        alert(a.panel.userAddError);
      }
    });
  };

  const regenerateToken = async (userId) => {
    if (!window.confirm(a.panel.regenerateConfirm)) return;

    try {
      const res = await apiFetch('/api/admin/regenerate-token', {
        method: 'POST',
        body: JSON.stringify({ userId })
      });
      const data = await res.json();
      if (!res.ok || !data.token) {
        alert(data.error || a.panel.regenerateFailed);
        return;
      }
      const link = `${window.location.origin}/?token=${data.token}`;
      await navigator.clipboard.writeText(link);
      setCopied(userId);
      setTimeout(() => setCopied(null), 2000);
      alert(a.panel.regenerateSuccess);
    } catch (err) {
      console.error(err);
      alert(a.panel.regenerateFailed);
    }
  };

  const toggleActive = (userId, currentStatus) => {
      fetch('/api/admin/toggle-active', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, isActive: !currentStatus })
    })
      .then(res => {
          if(res.ok) fetchUsers();
      });
  };

  const updateRole = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      const res = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      
      if (res.ok) {
        // Update local state immediately for responsiveness
        setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
        setRoleSaved(userId);
        setTimeout(() => setRoleSaved(null), 1500);
      } else {
        const error = await res.json();
        alert(`${a.panel.roleUpdateError}: ${error.error || a.common.unknownError}`);
        fetchUsers(); // Refresh to get correct state
      }
    } catch (err) {
      console.error('Role update error:', err);
      alert(a.panel.roleUpdateError);
      fetchUsers();
    } finally {
      setUpdatingRole(null);
    }
  };

  const copyLink = async (userId) => {
    try {
      const res = await apiFetch(`/api/admin/users/${userId}/magic-link`);
      const data = await res.json();
      if (!res.ok || !data.token) {
        alert(data.error || a.panel.linkFetchFailed);
        return;
      }
      const link = `${window.location.origin}/?token=${data.token}`;
      await navigator.clipboard.writeText(link);
      setCopied(userId);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error(err);
      alert(a.panel.linkCopyFailed);
    }
  };

  const deleteUser = async (userId) => {
    setDeleteError(null);
    try {
      const res = await fetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
      if (res.ok) {
        setUsers(users.filter(u => u.id !== userId));
        setDeleteConfirm(null);
      } else {
        const error = await res.json();
        // Use detailed message if available, otherwise use error field
        const errorMessage = error.message || error.error || a.common.deleteFailed;
        setDeleteError(errorMessage);
      }
    } catch (err) {
      console.error('Delete error:', err);
      setDeleteError(a.common.deleteFailed);
    }
  };

  return (
    <div className="card admin-panel">
      <h3><Settings className="icon"/> {a.panel.title}</h3>
      
      {/* Admin Navigation */}
      <div style={{display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap'}}>
        {!isBoardOnly && (
          <Link to="/admin/scoring" className="btn-secondary" style={{textDecoration: 'none'}}>
            <Calculator size={18} /> {a.panel.scoringSettings}
          </Link>
        )}
        <Link to="/admin/results" className="btn-secondary" style={{textDecoration: 'none'}}>
          <ClipboardList size={18} /> {a.panel.resultsMaintenance}
        </Link>
        {!isBoardOnly && (
          <>
            <Link to="/admin/settings" className="btn-secondary" style={{textDecoration: 'none'}}>
              <Cog size={18} /> {a.panel.systemSettings}
            </Link>
            {adsAdminEnabled && (
              <Link to="/admin/ads" className="btn-secondary" style={{textDecoration: 'none'}}>
                <Target size={18} /> {a.panel.adsManagement}
              </Link>
            )}
            <Link to="/admin/club" className="btn-secondary" style={{textDecoration: 'none'}}>
              <Palette size={18} /> {a.panel.clubSettings}
            </Link>
          </>
        )}
      </div>

      {isBoardOnly ? null : (
      <>
      <div className="add-user-form card" style={{padding: '1rem', background: '#f5f5f5'}}>
        <h4>{a.panel.addMember}</h4>
        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            <input 
            placeholder={a.panel.nicknamePlaceholder}
            value={newUser.name} 
            onChange={e => setNewUser({...newUser, name: e.target.value})} 
            style={{flex: 1}}
            />
            <select 
                value={newUser.role}
                onChange={e => setNewUser({...newUser, role: e.target.value})}
                style={{width: '140px'}}
            >
                <option value="member">{a.roles.member}</option>
                <option value="board">{a.roles.board}</option>
                <option value="admin">{a.roles.admin}</option>
            </select>
            <button onClick={addUser} className="btn-primary" style={{width: 'auto'}}>{a.common.create}</button>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content card" style={{ maxWidth: '450px', textAlign: 'center' }}>
            <AlertCircle size={48} color="#e74c3c" style={{ marginBottom: '1rem' }} />
            <h3>{a.panel.deleteUserTitle}</h3>
            
            {deleteError ? (
              <div style={{ 
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                padding: '1rem', 
                borderRadius: '6px',
                margin: '1rem 0',
                color: '#856404'
              }}>
                <strong>⚠️ {a.panel.deleteNotPossible}</strong>
                <div style={{ marginTop: '0.5rem' }}>
                  {deleteError}
                </div>
                <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#856404' }}>
                  <strong>{a.common.solution}</strong> {a.panel.deleteNotPossibleHint}
                </div>
              </div>
            ) : (
              <p style={{ color: '#666', margin: '1rem 0' }}>
                {a.panel.deleteWarning}
              </p>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="btn-secondary" onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}>{a.common.cancel}</button>
              {!deleteError && (
                <button 
                  className="btn-primary" 
                  style={{ background: '#e74c3c' }}
                  onClick={() => deleteUser(deleteConfirm)}
                >
                  <X size={16} /> {a.panel.deletePermanently}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h4 style={{ margin: 0 }}>{formatAdmin(t, a.panel.memberList, { count: filteredUsers.length })}</h4>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={showOnlyActive} 
            onChange={(e) => setShowOnlyActive(e.target.checked)}
          />
          {a.panel.showOnlyActive}
        </label>
      </div>
      
      <div className="table-responsive">
        <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
                <tr style={{textAlign: 'left', borderBottom: '2px solid #ddd'}}>
                    <th>{a.common.name}</th>
                    <th>{a.common.role}</th>
                    <th>{a.common.actions}</th>
                </tr>
            </thead>
            <tbody>
                {filteredUsers.map(u => (
                <tr key={u.id} style={{borderBottom: '1px solid #eee', opacity: u.isActive ? 1 : 0.5}}>
                    <td style={{padding: '10px 0'}}>
                        <strong>{u.name}</strong>
                        <br/>
                        <small style={{color: '#666'}}>{u.email}</small>
                        <div style={{ marginTop: '6px' }}>
                          <span className={`badge ${u.isActive ? 'active' : 'inactive'}`} style={{
                              background: u.isActive ? '#27ae60' : '#e74c3c',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '0.8rem'
                          }}>
                              {u.isActive ? a.common.active : a.common.blocked}
                          </span>
                        </div>
                    </td>
                    <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                          <select
                            value={u.role || 'member'}
                            onChange={(e) => updateRole(u.id, e.target.value)}
                            disabled={updatingRole === u.id}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              backgroundColor: roleSaved === u.id ? '#d4edda' : (updatingRole === u.id ? '#f0f0f0' : 'white'),
                              color: ROLE_COLORS[u.role] || '#333',
                              fontWeight: '500',
                              fontSize: '0.85rem',
                              cursor: updatingRole === u.id ? 'wait' : 'pointer',
                              transition: 'background-color 0.3s ease',
                              minWidth: '130px'
                            }}
                          >
                            <option value="member">{a.roles.member}</option>
                            <option value="board">{a.roles.board}</option>
                            <option value="admin">{a.roles.admin}</option>
                          </select>
                          {roleSaved === u.id && (
                            <Check size={14} color="#27ae60" style={{ position: 'absolute', right: '-20px' }} />
                          )}
                        </div>
                    </td>
                    <td>
                        <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                            <button className="btn-secondary" onClick={() => copyLink(u.id)} title={a.panel.copyLinkTitle}>
                                {copied === u.id ? <Check size={16} /> : <Copy size={16} />}
                            </button>
                            <button className="btn-secondary" onClick={() => regenerateToken(u.id)} title={a.panel.newLinkTitle}>
                                <RefreshCw size={16} />
                            </button>
                            <button className="btn-secondary" onClick={() => toggleActive(u.id, u.isActive)} title={u.isActive ? a.panel.blockTitle : a.panel.activateTitle}>
                                <Trash2 size={16} color={u.isActive ? '#e74c3c' : '#27ae60'} />
                            </button>
                            <button 
                              className="btn-secondary" 
                              onClick={() => setDeleteConfirm(u.id)} 
                              title={a.panel.deletePermanentlyTitle}
                              style={{ borderColor: '#e74c3c' }}
                            >
                                <X size={16} color="#e74c3c" />
                            </button>
                        </div>
                    </td>
                </tr>
                ))}
            </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
};

export default AdminPanel;
